/**
 * @module usePlatformColors
 * Singleton that provides platform-specific colours for UI chips and calendar events.
 *
 * Platform colours are stored in the `platforms` table in the sales database
 * (fields: `color`, `color2`, `color2_style`, `text_color`) and fetched once
 * from `/api/accounting/sales/options`. The singleton pattern (module-level `fetched` flag
 * and `fetchPromise` dedup) ensures only one HTTP request is ever made, even
 * when multiple components call `usePlatformColors()` simultaneously.
 *
 * Two-colour platforms support two background styles:
 * - "gradient" — CSS linear-gradient from color to color2
 * - "split"    — hard diagonal split (default), e.g. Noon's dual-brand identity
 */

import { ref } from "vue";
import api from "@/utils/api.js";

// ── Singleton state ───────────────────────────────────────────────────────────

const platforms = ref([]);
let fetched = false;
let fetchPromise = null; // Dedup concurrent calls before the first response

async function fetchPlatforms() {
  if (fetched) return;
  if (fetchPromise) return fetchPromise; // Already in-flight — share the promise
  fetchPromise = api.accountingApi
    .get("/api/accounting/sales/options")
    .then((res) => {
      platforms.value = res.data.platforms || [];
      fetched = true;
    })
    .catch(() => {}) // Silently fail — colours fall back to a default blue
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

// ── Color accessors ───────────────────────────────────────────────────────────

function getPlatformColor(name) {
  return platforms.value.find((p) => p.name === name)?.color || "#1976D2";
}

/**
 * Return a CSS background string — either a solid colour or a gradient/split
 * for platforms with two brand colours (e.g. Noon).
 */
function getPlatformGradient(name) {
  const p = platforms.value.find((pl) => pl.name === name);
  const c1 = p?.color || "#1976D2";
  const c2 = p?.color2;
  if (!c2) return c1;
  const style = p?.color2_style || "split";
  if (style === "gradient") {
    return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
  }
  // Hard split at 50% — a strict diagonal boundary rather than a smooth blend
  return `linear-gradient(135deg, ${c1} 50%, ${c2} 50%)`;
}

function getPlatformTextColor(name) {
  return platforms.value.find((p) => p.name === name)?.text_color || "#FFFFFF";
}

// ── Composable ────────────────────────────────────────────────────────────────

export function usePlatformColors() {
  // Trigger fetch on every composable call — idempotent due to the fetched flag
  fetchPlatforms();
  return {
    platforms,
    getPlatformColor,
    getPlatformGradient,
    getPlatformTextColor,
  };
}
